<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import { taskCategoriesApi, type TaskCategory } from '@/api/task-categories';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ListToolbar from '@/components/list-toolbar.vue';
import TaskCategoryForm from './task-category-form.vue';
import TaskCategoryDiscardForm from './task-category-discard-form.vue';
import TaskCategoryUndiscardForm from './task-category-undiscard-form.vue';

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const selectedCategory = ref<TaskCategory | null>(null);
const showForm = ref(false);
const scope = ref<'active' | 'discarded'>('active');
const hasActions = hasPermission.value('tasks', 'update') || hasPermission.value('tasks', 'destroy');
const {
  data: activeTaskCategories,
  refetch: refetchActiveCategories,
  isPending: isLoadingActiveCategories,
} = useQuery({
  queryKey: ['taskCategories', 'active'],
  queryFn: () => taskCategoriesApi.index('active'),
  initialData: [],
});

const {
  data: discardedTaskCategories,
  refetch: refetchDiscardedCategories,
  isPending: isLoadingDiscardedCategories,
} = useQuery({
  queryKey: ['taskCategories', 'discarded'],
  queryFn: () => taskCategoriesApi.index('discarded'),
  initialData: [],
});

const taskCategories = computed(() =>
  (scope.value === 'active' ? activeTaskCategories.value : discardedTaskCategories.value),
);

const isLoading = computed(() => isLoadingActiveCategories.value || isLoadingDiscardedCategories.value);

const actionSuccess = ref(false);

function openCreateForm() {
  selectedCategory.value = null;
  showForm.value = true;
}

function openEditForm(category: TaskCategory) {
  selectedCategory.value = category;
  showForm.value = true;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveCategories();
  refetchDiscardedCategories();
  actionSuccess.value = true;
}

function handleFormSuccess() {
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedCategory.value = null;
}

const headers: { title: string, key: string, sortable?: boolean }[] = [
  { title: 'Nombre', key: 'name' },
  { title: 'Descripción', key: 'description' },
  { title: '# Tareas', key: 'taskDefinitionsCount' },
  { title: 'Creada', key: 'humanCreatedAt' },
];
if (hasActions) {
  headers.push({ title: 'Acciones', key: 'actions', sortable: false });
}
</script>
<template>
  <v-card>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="taskCategories"
      :loading="isLoading"
      loading-text="Cargando categorías de tareas..."
    >
      <template #top>
        <list-toolbar title="Categorías de Tareas">
          <template #actions>
            <v-btn
              v-if="scope === 'active' && hasPermission('tasks', 'create')"
              class="text-white border bg-primary"
              @click="openCreateForm"
            >
              <v-icon
                start
                icon="mdi-plus"
              />
              Agregar Categoría
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                Activas
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                Archivadas
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
      </template>
      <template #[`item.description`]="{ item }">
        <p class="text-wrap description-cell">
          {{ item.description || '—' }}
        </p>
      </template>

      <template #[`item.taskDefinitionsCount`]="{ item }">
        <v-chip
          :color="item.taskDefinitionsCount > 0 ? 'primary' : 'grey'"
          size="small"
        >
          {{ item.taskDefinitionsCount }}
        </v-chip>
      </template>

      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active' && hasActions"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('tasks', 'update')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('tasks', 'destroy')"
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
                variant="text"
                color="warning"
              />
            </template>
            <template #default="{ isActive }">
              <task-category-discard-form
                :task-category="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
        </div>
        <v-dialog
          v-else-if="scope === 'discarded' && hasActions"
          max-width="600"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="hasPermission('tasks', 'update')"
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
              variant="text"
              color="success"
            />
          </template>
          <template #default="{ isActive }">
            <task-category-undiscard-form
              :task-category="item"
              @close="isActive.value = false"
              @success="handleDiscardAndUndiscardSuccess"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="600"
      scrollable
    >
      <task-category-form
        :task-category="selectedCategory"
        @close="handleFormClose"
        @success="handleFormSuccess"
      />
    </v-dialog>
  </v-card>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
<style scoped>
.description-cell {
  max-width: 300px;
}
</style>
